import { useRef, useState, useEffect, useCallback } from 'react'

import {
  NetworkStatus,
  Space,
  TemplateRequestStatus,
} from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import {
  useCollections,
  useCreateSpaceFromTemplate,
  useInvalidateCollectionsQuery,
  useNetwork,
  useTemplateRequest,
} from '@tribeplatform/react-sdk/hooks'
import { Alert } from '@tribeplatform/react-ui-kit/Alert'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { Link } from '@tribeplatform/react-ui-kit/Link'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { Form } from '../../Form/index.js'
import { T } from '../../i18n/components/T.js'
import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { useNavigate } from '../../Link/index.js'

export interface CreatePageModalProps {
  open: boolean
  templateId: string
  collectionId?: string
  onClose: () => void
  setLoading: () => void
}

export const CreatePageModal = ({
  open,
  templateId,
  collectionId,
  onClose,
  setLoading,
}: CreatePageModalProps) => {
  const { $t } = useI18n()
  const inputRef = useRef<HTMLInputElement>()
  const navigate = useNavigate()
  const invalidateCollections = useInvalidateCollectionsQuery()
  const { data: network } = useNetwork({
    fields: {
      limits: 'all',
      usages: 'all',
    },
  })
  const { Link: RouterLink } = useRouter()

  const spaceLimitReached =
    network?.usages?.usages.spacesCapacity >=
      (network?.limits?.usageLimits?.spacesCapacity ?? Infinity) &&
    network?.status === NetworkStatus.published

  const { data: collections } = useCollections({
    fields: {},
    variables: {},
  })
  const [templateRequestId, setTemplateRequestId] = useState(null)
  const [createSpaceMessage, setCreateSpaceMessage] = useState(null)

  const {
    mutateAsync: createSpaceFromTemplate,
    isLoading: isLoadingFromTemplate,
  } = useCreateSpaceFromTemplate()
  const { data: templateRequest } = useTemplateRequest({
    fields: {
      createdEntity: 'basic',
    },
    variables: {
      id: templateRequestId,
    },
    useQueryOptions: {
      enabled: !!templateRequestId,
      refetchInterval: 1000,
      onError: () => {
        toast({
          title: $t({
            id: 'Generics.SomethingWentWrong',
            defaultMessage: 'Something went wrong',
          }),
          status: 'error',
        })
        setTemplateRequestId(null)
      },
    },
  })

  const onFulfilled = useCallback(() => {
    invalidateCollections()
    onClose()
    toast({
      title: $t({
        id: 'Space.CreateSpaceModal.CreatedTitle',
        defaultMessage: '{SPACE_CC} created successfully!',
      }),
      description: $t({
        id: 'Space.CreateSpaceModal.CreatedDescription',
        defaultMessage:
          'You can now customize it to your liking using the customizer.',
      }),
      status: 'success',
    })
    setTimeout(() => {
      navigate(
        `/manage/customizer/spaces/${templateRequest?.createdEntity?.id}/blocks`,
      )
    }, 500)
  }, [
    $t,
    invalidateCollections,
    navigate,
    onClose,
    templateRequest?.createdEntity?.id,
  ])

  useEffect(() => {
    if (templateRequest?.status === TemplateRequestStatus.Succeeded) {
      setTemplateRequestId(null)
      setCreateSpaceMessage('Done!')
      onFulfilled()
    } else if (templateRequest?.status === TemplateRequestStatus.Failed) {
      setTemplateRequestId(null)
      onClose()
      toast({
        title: $t({
          id: 'Space.CreateSpaceModal.CreateFailedTitle',
          defaultMessage: 'Something went wrong while creating the {SPACE}',
        }),
        description: $t({
          id: 'Generics.TryAgainLater',
          defaultMessage: 'Please try again later.',
        }),
        status: 'error',
      })
    } else if (templateRequest?.status === TemplateRequestStatus.InProgress) {
      if (templateRequest?.createdEntityId) {
        setCreateSpaceMessage(
          $t({
            id: 'Space.CreateSpaceModal.WrappingUp',
            defaultMessage: 'Wrapping up...',
          }),
        )
      }
    }
  }, [templateRequest, onFulfilled, onClose, $t])

  return (
    <Modal open={open} onClose={onClose} initialFocus={inputRef}>
      <Modal.Header
        title={$t({
          id: 'Space.CreateSpaceModal.HeaderTitle',
          defaultMessage: 'Create a new {SPACE}',
        })}
      />
      <Modal.Content>
        <Form<Space & { collectionId?: string }>
          defaultValues={{
            collectionId: collectionId ?? null,
            private: true,
            hidden: true,
          }}
          onSubmit={async data => {
            const {
              name,
              collectionId,
              description,
              private: _private,
              hidden,
            } = data
            setLoading()
            setCreateSpaceMessage(
              $t({
                id: 'Templates.CreateSpaceModal.CratingSpaceMessage',
                defaultMessage: 'Creating the {SPACE}...',
              }),
            )
            const templateRequest = await createSpaceFromTemplate({
              input: {
                name,
                description,
                private: _private,
                hidden,
                collectionId,
              },
              templateId,
              style: '',
            })
            setTemplateRequestId(templateRequest.id)
          }}
        >
          {({ watch, setValue, setError }) => {
            const [isPrivate] = watch(['private'])
            if (!isPrivate) {
              setValue('hidden', false)
            }
            return (
              <div className="flex flex-col space-y-5">
                <Form.Input
                  ref={inputRef}
                  label={$t({
                    id: 'Generics.Name.Noun',
                    defaultMessage: 'Name',
                  })}
                  name="name"
                  validation={{
                    required: $t({
                      id: 'Space.CreateSpaceModal.NameFieldRequiredMessage',
                      defaultMessage: 'Please enter a name for the {SPACE}',
                    }),
                  }}
                  placeholder={$t({
                    id: 'Space.CreateSpaceModal.NameFieldPlaceholder',
                    defaultMessage: 'e.g. Say Hello',
                  })}
                />
                <Form.Textarea
                  label={$t({
                    id: 'Generics.Description',
                    defaultMessage: 'Description',
                  })}
                  name="description"
                  placeholder={$t({
                    id: 'Space.CreateSpaceModal.DescriptionPlaceholder',
                    defaultMessage:
                      'What is this {SPACE} about? (e.g. Introduce yourself and get to know other {MEMBERS})',
                  })}
                />
                <Form.Toggle
                  helperText={$t({
                    id: 'Space.CreateSpaceModal.MakePrivateToggleHelperText',
                    defaultMessage:
                      'Only {MEMBERS} can see who`s in the {SPACE} and what they post.',
                  })}
                  label={$t({
                    id: 'Space.CreateSpaceModal.MakePrivateToggleLabel',
                    defaultMessage: 'Make private',
                  })}
                  name="private"
                />
                {isPrivate && (
                  <>
                    <Form.Toggle
                      helperText={$t({
                        id: 'Space.CreateSpaceModal.HideSpaceToggleHelperText',
                        defaultMessage:
                          'Hide this {SPACE} from non {SPACE} {MEMBERS}.',
                      })}
                      label={$t({
                        id: 'Space.CreateSpaceModal.HideSpaceToggleLabel',
                        defaultMessage: 'Hide {SPACE}',
                      })}
                      name="hidden"
                    />

                    <Alert
                      status="neutral"
                      icon={<SvgIcon name="status-info" />}
                    >
                      <T
                        id="Space.CreateSpaceModal.HideSpaceAlertText"
                        defaultMessage="It`s recommended to start your {SPACE} as a hidden one and unhide it when it`s ready."
                      />
                    </Alert>
                  </>
                )}
                <Form.Select
                  label={$t({
                    id: 'Generics.Collection',
                    defaultMessage: '{COLLECTION_CC}',
                  })}
                  name="collectionId"
                  placeholder={$t({
                    id: 'Space.CreateSpaceModal.SelectCollectionPlaceholder',
                    defaultMessage: 'Select a {COLLECTION}...',
                  })}
                  validation={{
                    validate: value => {
                      if (value !== undefined) {
                        return true
                      }
                      setTimeout(() => {
                        setError('collectionId', {
                          type: 'validate',
                          message: $t({
                            id: 'Space.CreateSpaceModal.SelectCollectionValidationMessage',
                            defaultMessage:
                              'Please select a {COLLECTION} or pick "No {COLLECTION}".',
                          }),
                        })
                      }, 10)
                      return false
                    },
                  }}
                  items={[
                    {
                      text: $t({
                        id: 'Space.CreateSpaceModal.SelectCollectionItemNoCollectionText',
                        defaultMessage: 'No {COLLECTION}',
                      }),
                      value: null,
                    },
                    ...(collections || [])?.map(collection => ({
                      text: collection?.name,
                      value: collection?.id,
                    })),
                  ]}
                />
                {spaceLimitReached && (
                  <Alert status="info">
                    <T
                      defaultMessage="You cannot create a new space because you have reached the limits in your plan. <link>Manage usages</link>."
                      id="Space.CreateSpaceModal.SpaceLimitReachedMessage"
                      values={{
                        link: chunks => (
                          <Link as={RouterLink} href="/manage/billing/usages">
                            {chunks}
                          </Link>
                        ),
                      }}
                    />
                  </Alert>
                )}
                <Form.Actions className="items-center">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={
                      isLoadingFromTemplate ||
                      templateRequestId ||
                      spaceLimitReached
                    }
                  >
                    <T id="Generics.Create" defaultMessage="Create" />
                  </Button>
                  <Button variant="secondaryNeutral" onClick={onClose}>
                    <T id="Generics.Cancel" defaultMessage="Cancel" />
                  </Button>
                  {createSpaceMessage ? (
                    <div className="flex-1 flex space-s-2 items-center">
                      <SvgIcon className="animate-spin" name="spinner" />{' '}
                      <span>{createSpaceMessage}</span>
                    </div>
                  ) : null}
                </Form.Actions>
              </div>
            )
          }}
        </Form>
      </Modal.Content>
    </Modal>
  )
}
